// 三井住友カードの利用明細CSVをZeniの取引インポート形式に変換する。
//
// Vpassからダウンロードできる利用明細CSVは Shift-JIS で、ヘッダー行を持たず、
// 先頭に会員名・マスクされたカード番号・カード名称のサマリ行が1行だけ付く:
//   大澤　悟　様,4980-05**-****-****,三井住友カードＶＩＳＡ（ＮＬ）
// 明細行はヘッダー名の無い固定順の列を持つ:
//   利用日, 利用店名, 利用金額, 支払回数, 今回回数, 今回支払金額, 備考
// 末尾には利用日・利用店名が空で、合計額だけが今回支払金額の位置に入る合計行が付く。
// 楽天カード等と同じくCSVに口座・カテゴリ情報が無いため、口座は呼び出し側で指定し、
// カテゴリは利用店名のキーワードから推定する（推定できなければ「その他(other)」）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type SmbcCardCategoryRef = CsvCategoryRef
export type SmbcCardImportRow = CsvImportRow
export type SmbcCardParseResult = CsvParseResult

interface ColumnMap {
  date: number
  desc: number
  amount: number
  payCount: number
  installment: number
  note: number
}

// 通常フォーマットにはキーワードのヘッダー行が無いため、既定は固定位置を使う。
const DEFAULT_COLUMNS: ColumnMap = { date: 0, desc: 1, amount: 2, payCount: 3, installment: 4, note: 6 }

// ヘッダー行を持つバリエーション（Vpassの別画面等）にも備え、見つかればそちらの列位置を優先する。
function locateColumns(header: string[]): ColumnMap {
  const find = (keywords: string[], fallback: number) => {
    const idx = header.findIndex((h) => keywords.some((k) => h.includes(k)))
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find(['利用日', '日付'], DEFAULT_COLUMNS.date),
    desc: find(['利用店名', '店名'], DEFAULT_COLUMNS.desc),
    amount: find(['利用金額'], DEFAULT_COLUMNS.amount),
    payCount: find(['支払回数'], DEFAULT_COLUMNS.payCount),
    installment: find(['今回回数'], DEFAULT_COLUMNS.installment),
    note: find(['備考'], DEFAULT_COLUMNS.note),
  }
}

// キーワードのヘッダー行があればその次から、無ければ先頭列が日付として解釈できる
// 最初の行（＝サマリ行の次の明細行）からデータ開始位置を判定する。
function findDataStart(lines: string[]): { start: number; columns: ColumnMap } {
  const limit = Math.min(lines.length, 10)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i])
    const hasDate = cells.some((c) => c.includes('利用日') || c.includes('日付'))
    const hasAmount = cells.some((c) => c.includes('利用金額'))
    if (hasDate && hasAmount) return { start: i + 1, columns: locateColumns(cells) }
  }
  for (let i = 0; i < limit; i++) {
    if (normalizeDate(splitCsvLine(lines[i])[0] ?? '') !== null) return { start: i, columns: DEFAULT_COLUMNS }
  }
  return { start: 0, columns: DEFAULT_COLUMNS }
}

// 一括払い（支払回数「１」）は既定なのでメモに残さない。分割払いのみ回数を記録する。
const ONE_TIME_PAY_COUNTS = new Set(['1'])

// 支払回数・今回回数・備考をメモへ退避する。1回払いかつ備考が無ければ未設定。
function buildNote(payCount: string, installment: string, note: string): string | undefined {
  const parts: string[] = []
  const normalizedPayCount = payCount.normalize('NFKC').trim()
  if (normalizedPayCount !== '' && !ONE_TIME_PAY_COUNTS.has(normalizedPayCount)) {
    const times = installment.normalize('NFKC').trim()
    parts.push(
      times !== '' && times !== normalizedPayCount
        ? `分割${normalizedPayCount}回（今回${times}回目）`
        : `分割${normalizedPayCount}回`,
    )
  }
  const trimmedNote = note.trim()
  if (trimmedNote !== '') parts.push(trimmedNote)
  return parts.length > 0 ? parts.join(' / ') : undefined
}

export function parseSmbcCardCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<SmbcCardCategoryRef>,
): SmbcCardParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const { start, columns: col } = findDataStart(lines)

  const { expenseIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)

  const rows: SmbcCardImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(start)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')
    const rawAmount = parseAmount(cols[col.amount] ?? '')
    if (!date || !Number.isFinite(rawAmount) || rawAmount === 0) {
      ignored++
      continue
    }

    const description = (cols[col.desc] ?? '').trim() || '三井住友カード利用'
    const isIncome = rawAmount < 0 // マイナス計上（返金等）は収入扱い
    const amount = Math.abs(rawAmount)

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category: isIncome
        ? fallbackIncome
        : matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet) ?? fallbackExpense,
      description,
      amount,
      accountId,
      currency: 'JPY',
      note: buildNote(cols[col.payCount] ?? '', cols[col.installment] ?? '', cols[col.note] ?? ''),
    })
  }

  return { rows, parsed: rows.length, ignored }
}
